/**
 * # Menu
 *
 * A dropdown menu component that displays a trigger element and menu content when triggered.
 * The component exposes several sub-components for composition: `Menu.Trigger`, `Menu.Content`, `Menu.Item`, and `Menu.Divider`.
 * For static (non-dropdown) menus, use `Menu.Static`.
 *
 * @see [Design ticket]{@link https://github.com/albert-io/nexus/issues/12952}
 *
 *
 * ## Props
 *
 * - `children: React.ReactNode` - The content of the Menu (should include Menu.Trigger and Menu.Content).
 * - `className?: string` (optional) - Additional CSS class name for the Menu
 * - `position?: string` - The position of the dropdown relative to the trigger element.
 * - `positionFixed?: boolean` - Whether the dropdown should have a fixed position.
 * - `onDismiss?: (e: Event) => void` - Callback function when the dropdown is dismissed.
 * - `wrapperClassName?: string` - Additional CSS class name for the dropdown wrapper.
 * - `closeOnItemClick?: boolean` - Whether to close the dropdown when an item is clicked.
 * - `onToggleCallback?: (isOpen: boolean) => void` - Callback when dropdown state changes.
 *
 * ### `Menu.Trigger` Props
 *
 * - `children: (props: TriggerRenderProps) => ReactNode` - Render prop that receives trigger event handlers and ARIA props.
 * - `className?: string` - Additional CSS class name for the trigger wrapper.
 * - `fillWrapper?: boolean` - Whether the trigger should fill its wrapper element.
 *
 * ### `Menu.Content` Props
 *
 * - `children: ReactNode` - The menu items and dividers.
 * - `className?: string` - Additional CSS class name for the content wrapper.
 *
 * ### `Menu.Item` Props
 *
 * - `as?: C`; The HTML element or React component to be rendered as the root element of the component. Default is `div`.
 * - `className?: string`; CSS classname.
 * - `children: ReactNode`; Inner content.
 * - `selected?: boolean;` Whether the item is selected. Default is `false`.
 * - `disabled?: boolean;` Whether the item is disabled. Default is `false`.
 * - `readonly?: boolean;` Makes the item non-interactive (no hover states, no click handling). Use this for display-only items vs `disabled` for temporarily unavailable actions. Default is `false`.
 * - `onClick?: (e: MouseEvent<HTMLElement>) => void;` Click handler for the item.
 * - `subLabel?: string;` Optional sublabel text to display below the main label.
 * - `textProps?: Partial<TextProps>;` Additional props to be passed to the main Text component.
 * - `subtextProps?: Partial<TextProps>;` Additional props to be passed to the sublabel Text component.
 * - `leftIconProps?: IconProps;` Props for an optional left icon.
 * - `rightIconProps?: IconProps;` Props for an optional right icon.
 * - `padding?: 'none' | 'normal';` Padding level for the menu item. Default is 'normal'.
 * - `preventDefaultClickHandler?: boolean;` Whether or not the item will `preventDefault` on the click event. You should only ever need to set this to `false` if you need the original click event to propagate to your provided `as` element (ie. `<a>` or `<Link>`). Default is `true`.
 * - `disableHover?: boolean;` Whether to disable hover styles for the item. Default is `false`.
 * - `disableActive?: boolean;` Whether to disable active styles for the item. Default is `false`.
 *
 * ### `Menu.Divider` Props
 *
 * - `className?: string`; CSS classname.
 * - `fullWidth?: boolean;` Whether the divider should span the full width of the menu. Default is `false`.
 *
 * ### `Menu.Static` Props
 *
 * - `children: React.ReactNode` - The static menu content.
 * - `className?: string` - Additional CSS class name for the static menu.
 *
 * ## Usage
 *
 * ```tsx
 * import Menu from './Menu.react';
 *
 * const DropdownExample = () => {
 *   return (
 *     <Menu closeOnItemClick>
 *       <Menu.Trigger>
 *         {(triggerProps) => (
 *           <Button {...triggerProps}>
 *             Open Menu
 *           </Button>
 *         )}
 *       </Menu.Trigger>
 *       <Menu.Content>
 *         <Menu.Item onClick={() => console.log('Item 1')}>
 *           Menu Item 1
 *         </Menu.Item>
 *         <Menu.Divider />
 *         <Menu.Item onClick={() => console.log('Item 2')}>
 *           Menu Item 2
 *         </Menu.Item>
 *       </Menu.Content>
 *     </Menu>
 *   );
 * };
 *
 * const StaticExample = () => {
 *   return (
 *     <Menu.Static>
 *       <Menu.Item>Static Item 1</Menu.Item>
 *       <Menu.Item>Static Item 2</Menu.Item>
 *     </Menu.Static>
 *   );
 * };
 * ```
 */

import {WindowSizeConsumer} from '@albert-io/atomic/helpers/WindowSizeProvider/WindowSizeProvider.react';
import WithToggle from '@albert-io/atomic/helpers/WithToggle/WithToggle.react';
import ToggleStateNotifier from '@albert-io/atomic/helpers/WithToggle/ToggleStateNotifier.react';
import type {Placement} from '@popperjs/core';
import cx from 'classnames';
import type {ReactNode} from 'react';
import React from 'react';
import {Manager} from 'react-popper';

import Content from './MenuContent.react';
import type {MenuPopperProps} from './MenuContext.react';
import {MenuProvider} from './MenuContext.react';
import Item from './MenuItem.react';
import Static from './MenuStatic.react';
import Trigger from './MenuTrigger.react';

import './menu.scss';

interface DividerProps extends PropsWithClassNameOptional {
  fullWidth?: boolean;
}

const Divider = ({fullWidth, className = ''}: DividerProps) => (
  <div
    className={cx(className, {
      'o-menu__divider-container--fullwidth py-x1': fullWidth,
      'px-x1 py-x0-half': !fullWidth
    })}
    role='separator'
    aria-orientation='horizontal'
  >
    <hr className='o-menu__divider' />
  </div>
);
interface MenuProps {
  children: ReactNode;
  className?: string;
  position?: Placement;
  positionFixed?: boolean;
  popperProps?: MenuPopperProps;
  onDismiss?: (e?: Event) => void;
  wrapperClassName?: string;
  closeOnItemClick?: boolean;
  onToggleCallback?: (isOpen: boolean) => void;
}

const Menu = Object.assign(
  ({
    children,
    className,
    position = 'bottom-start',
    positionFixed = false,
    popperProps,
    onDismiss,
    wrapperClassName,
    closeOnItemClick = false,
    onToggleCallback = () => {}
  }: MenuProps) => {
    return (
      <WithToggle className={wrapperClassName} onDismiss={onDismiss}>
        {({onClick: onToggle, on: isOpen}) => {
          const closeHandler =
            isOpen && typeof onDismiss === 'function'
              ? (e?: Event) => {
                  onDismiss(e);
                  onToggle();
                }
              : onToggle;

          const menuContextValue = {
            isOpen,
            toggle: onToggle,
            close: closeHandler,
            closeOnItemClick,
            position,
            positionFixed,
            popperProps
          };

          return (
            <>
              <ToggleStateNotifier on={isOpen} onToggleCallback={onToggleCallback} />
              <WindowSizeConsumer>
                {(context) => {
                  if (!context) return null;

                  return (
                    <MenuProvider value={menuContextValue}>
                      <div className={cx('o-menu', className)}>
                        <Manager>{children}</Manager>
                      </div>
                    </MenuProvider>
                  );
                }}
              </WindowSizeConsumer>
            </>
          );
        }}
      </WithToggle>
    );
  },
  {
    Content,
    Divider,
    Item,
    Static,
    Trigger
  }
);

(Menu as {displayName?: string}).displayName = 'Menu';

export default Menu;
